import type { ApiKey, AppDashboardSummary, AppModelUsage, AppTab, BillingFundingSummary, CheckoutPayload, MemberEntitlementsSummary, MemberEntitlement, ModelUsagePeriods, Order, PaginationMetadata, RequestLog, TokenWallet, UsageLimitConfig, WalletTransaction } from "@/types";
import { api } from "@/api";
import { renderUserGuide, SEEKTOKEN_GATEWAY_BASE_URL } from "@/docs";
import { els, state } from "@/state";
import { closeModal, copyText, openModal, renderWalletTransactionTable, setBusy, toast } from "@/views/admin";
import { emptyUsageSummary, escapeHtml, fmtInt, fmtNum, formatMicrocreditsAsCredits, parseUserRoles, saveStoredPlainApiKeys } from "@/utils";
import { showUsageLimitEditor } from "@/views/usageLimitEditor";
import { renderModelUsageCharts } from "@/views/modelUsageChart";

function formatWalletCredits(value: number | null | undefined): string {
  return formatMicrocreditsAsCredits(value);
}

/** 渲染用户控制台主页面 */
export function renderAppPage(): void {
  if (!state.user) return;
  // 用户徽标
  if (els.appUserBadge) {
    els.appUserBadge.innerHTML = `<strong>${escapeHtml(state.user.name || state.user.email)}</strong>`;
  }
  // admin 角色显示管理后台入口
  const roles = parseUserRoles(state.user);
  if (els.appAdminLink) {
    els.appAdminLink.classList.toggle("hidden", !roles.includes("admin"));
  }
  // 渲染当前 tab
  renderAppTab(state.appTab);
}

/** 切换用户控制台子标签 */
export function renderAppTab(tab: AppTab): void {
  state.appTab = tab;
  document.querySelectorAll<HTMLElement>(".app-nav-item").forEach((btn) => {
    btn.classList.toggle("active", btn.dataset.appTab === tab);
  });

  if (!els.appContent) return;
  if (tab === "overview") renderAppOverview();
  else if (tab === "keys") renderAppKeys();
  else if (tab === "entitlements") void renderAppEntitlements();
  else if (tab === "wallet") void renderAppWallet();
  else if (tab === "orders") void renderAppOrdersPage();
  else if (tab === "usage") void renderAppUsage();
  else if (tab === "modelUsage") void renderAppModelUsage();
  else if (tab === "docs") renderAppDocs();
  else if (tab === "settings") renderAppSettings();
}

function formatRechargeAmount(value: number): string {
  return `¥${Number(value || 0).toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** 概览:用户 API Key、Token、充值与计费汇总 + 快速开始文档 */
export function renderAppOverview(): void {
  const dashboard = state.appDashboard;
  const usage = dashboard?.tokenUsage || {
    total: emptyUsageSummary(),
    month: emptyUsageSummary(),
    week: emptyUsageSummary(),
    day: emptyUsageSummary()
  };
  const recharge = dashboard?.recharge || { totalAmount: 0, monthAmount: 0, currency: "CNY" };
  const billing = dashboard?.billing || { totalMicrocredits: 0 };
  const points = dashboard?.points || { totalConsumed: 0, monthConsumed: 0 };
  const funding = state.appFundingSummary;
  if (!els.appContent) return;
  els.appContent.innerHTML = `
    <section class="overview-grid">
      <div class="overview-card">
        <p class="eyebrow">总可用 Credits</p>
        <p class="overview-value">${formatWalletCredits(funding?.totalAvailablePoints || 0)}</p>
        <p class="muted">月卡优先，钱包补差</p>
      </div>
      <div class="overview-card">
        <p class="eyebrow">月卡可用 Credits</p>
        <p class="overview-value">${formatWalletCredits(funding?.monthlyPass?.availablePoints || 0)}</p>
        <p class="muted">${funding?.monthlyPass?.active ? `到期 ${escapeHtml(formatTime(funding.monthlyPass.expiresAt))}` : "暂无活动月卡"}</p>
      </div>
      <div class="overview-card">
        <p class="eyebrow">钱包可用 Credits</p>
        <p class="overview-value">${formatWalletCredits(funding?.wallet?.availablePoints || 0)}</p>
        <p class="muted">预占 ${formatWalletCredits(funding?.wallet?.reservedPoints || 0)}</p>
      </div>
      <div class="overview-card">
        <p class="eyebrow">API Key 数</p>
        <p class="overview-value">${state.userApiKeys.length}</p>
      </div>
      <div class="overview-card">
        <p class="eyebrow">Token 总消耗</p>
        <p class="overview-value">${fmtNum(usage.total.totalTokens)}</p>
      </div>
      <div class="overview-card">
        <p class="eyebrow">当月 Token 消耗</p>
        <p class="overview-value">${fmtNum(usage.month.totalTokens)}</p>
      </div>
      <div class="overview-card">
        <p class="eyebrow">当周 Token 消耗</p>
        <p class="overview-value">${fmtNum(usage.week.totalTokens)}</p>
      </div>
      <div class="overview-card">
        <p class="eyebrow">当日 Token 消耗</p>
        <p class="overview-value">${fmtNum(usage.day.totalTokens)}</p>
      </div>
      <div class="overview-card">
        <p class="eyebrow">总充值</p>
        <p class="overview-value">${formatRechargeAmount(recharge.totalAmount)}</p>
      </div>
      <div class="overview-card">
        <p class="eyebrow">当月充值</p>
        <p class="overview-value">${formatRechargeAmount(recharge.monthAmount)}</p>
      </div>
      <div class="overview-card">
        <p class="eyebrow">总计费</p>
        <p class="overview-value">${formatWalletCredits(billing.totalMicrocredits)}</p>
        <p class="muted">credits（含待结算）</p>
      </div>
      <div class="overview-card">
        <p class="eyebrow">总积分消耗</p>
        <p class="overview-value">${formatWalletCredits(points.totalConsumed)}</p>
        <p class="muted">credits（已结算）</p>
      </div>
      <div class="overview-card">
        <p class="eyebrow">当月积分消耗</p>
        <p class="overview-value">${formatWalletCredits(points.monthConsumed)}</p>
        <p class="muted">credits（已结算）</p>
      </div>
    </section>
    <section class="panel panel-fill">
      <div class="panel-heading">
        <div><p class="eyebrow">Quick Start</p><h2>快速开始</h2></div>
      </div>
      <div class="docs-content">
        <h3>1. 创建 API Key</h3>
        <p>切换到「API Key」标签页,点击「创建 API Key」按钮。Key 明文只显示一次,请立即保存。</p>
        <h3>2. 配置 config.toml</h3>
        <pre><code>model = "gpt-5.4"
model_provider = "seektoken"

[model_providers.seektoken]
name = "SeekToken"
base_url = "${escapeHtml(SEEKTOKEN_GATEWAY_BASE_URL)}"
wire_api = "responses"
requires_openai_auth = true</code></pre>
        <h3>3. 配置 auth.json</h3>
        <pre><code>{
  "auth_mode": "apikey",
  "OPENAI_API_KEY": "SK_你的Key"
}</code></pre>
        <h3>4. 使用 Codex</h3>
        <pre><code>codex --model seektoken/gpt-5.4 "你的任务"</code></pre>
        <div class="docs-callout">
          <strong>更完整的接入说明</strong>
          <p>两份文件的具体路径，以及 macOS、Linux、Windows、WSL 和服务器脚本，请切换到上方 <code>使用文档</code> 标签页查看。</p>
        </div>
      </div>
    </section>
  `;
}

/** 加载用户首页的完整汇总，避免用分页流水推算累计数据。 */
export async function loadAppDashboardSummary(): Promise<void> {
  if (!state.user) return;
  try {
    const [dashboard, funding] = await Promise.all([
      api<AppDashboardSummary>("/api/app/dashboard-summary"),
      api<BillingFundingSummary>("/api/billing/funding-summary")
    ]);
    state.appDashboard = dashboard;
    state.appFundingSummary = funding;
  } catch {
    state.appDashboard = null;
    state.appFundingSummary = null;
  }
}

export function renderAppDocs(): void {
  if (!els.appContent) return;
  els.appContent.innerHTML = renderUserGuide();
}

/** API Key 列表 */
export function renderAppKeys(): void {
  const keys: ApiKey[] = state.userApiKeys || [];
  if (!els.appContent) return;
  els.appContent.innerHTML = `
    <section class="panel panel-fill">
      <div class="panel-heading">
        <div><p class="eyebrow">API Keys</p><h2>我的 API Key</h2></div>
        <button class="primary-button" data-action="create-user-key">创建 API Key</button>
      </div>
      ${keys.length === 0 ? `<p class="muted pad">还没有 API Key,点击右上角创建。</p>` : `
        <div class="table-wrap">
          <table class="sub-table app-key-table">
            <colgroup><col class="app-key-column-identity"><col class="app-key-column-usage"><col class="app-key-column-limits"><col class="app-key-column-times"><col class="app-key-column-actions"></colgroup>
            <thead>
              <tr>
                <th>Key</th>
                <th>用量</th>
                <th>我的用量限制</th>
                <th>时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              ${keys.map((k) => {
                const plainKey = state.plainApiKeys[k.id];
                return `
                  <tr>
                    <td>
                      <div class="app-key-identity">
                        <div class="app-key-name"><strong>${escapeHtml(k.name)}</strong><span class="pill ${k.status === "active" ? "pill-available" : "pill-warn"}">${k.status === "active" ? "启用" : "已停用"}</span></div>
                        <div class="app-key-secret">
                          <code>${escapeHtml(k.keyPrefix)}...</code>
                          ${plainKey ? `
                            <button class="ghost-button compact-button" data-action="toggle-show-key" data-id="${escapeHtml(k.id)}">显示完整 Key</button>
                            <code class="key-value hidden" id="full-key-${escapeHtml(k.id)}">${escapeHtml(plainKey)}</code>
                          ` : `
                            <button class="ghost-button compact-button" data-action="fetch-key" data-id="${escapeHtml(k.id)}">获取完整 Key</button>
                          `}
                        </div>
                      </div>
                    </td>
                    <td><div class="app-key-usage-summary"><div><span>今日</span>${renderAppKeyUsageCell(k.usageLimit, "day")}</div><div><span>本月</span>${renderAppKeyUsageCell(k.usageLimit, "month")}</div></div></td>
                    <td>
                      <div class="app-key-limit">
                        <strong>${usageLimitConfigText(k.usageLimit?.userLimits)}</strong>
                        <small>仅设置此 Key 的自有上限</small>
                        <button class="ghost-button compact-button" data-action="edit-user-key-usage-limits" data-id="${escapeHtml(k.id)}">设置我的限制</button>
                      </div>
                    </td>
                    <td class="table-muted"><div class="app-key-times"><span>最近 ${escapeHtml(formatTime(k.lastUsedAt))}</span><span>创建 ${escapeHtml(formatTime(k.createdAt))}</span></div></td>
                    <td>
                      <div class="app-key-actions">
                        <button class="secondary-button compact-button" data-action="copy-user-key" data-id="${escapeHtml(k.id)}">复制</button>
                        <button class="ghost-button compact-button" data-action="toggle-user-key" data-id="${escapeHtml(k.id)}" data-status="${escapeHtml(k.status)}">${k.status === "active" ? "停用" : "启用"}</button>
                        <button class="ghost-button compact-button danger-action" data-action="delete-user-key" data-id="${escapeHtml(k.id)}">删除</button>
                      </div>
                    </td>
                  </tr>
                `;
              }).join("")}
            </tbody>
          </table>
        </div>
      `}
    </section>
  `;
}

function renderAppKeyUsageCell(summary: ApiKey["usageLimit"], period: "day" | "month"): string {
  if (!summary) return `<span class="muted">限制数据加载中</span>`;
  const usage = summary[period];
  const limit = period === "day" ? summary.limits.dailyCreditLimit : summary.limits.monthlyCreditLimit;
  const used = usage.settledCredits + usage.reservedCredits;
  return `
    <div class="app-key-usage">
      <strong>${formatWalletCredits(used)} / ${limit === null ? "不限" : formatWalletCredits(limit)}</strong>
      <small>${formatWalletCredits(usage.settledCredits)} 已结算 · ${formatWalletCredits(usage.reservedCredits)} 预占</small>
    </div>
  `;
}

function usageLimitConfigText(config: UsageLimitConfig | undefined): string {
  if (!config) return "均不限制";
  const value = (amount: number | null, unit = "") => amount === null ? "不限" : `${formatWalletCredits(amount)}${unit}`;
  return `日 ${value(config.dailyCreditLimit)} · 月 ${value(config.monthlyCreditLimit)} · 并发 ${config.maxConcurrentRequests ?? "不限"}`;
}

export async function editUserApiKeyUsageLimits(keyId: string): Promise<void> {
  const key = state.userApiKeys.find((item) => item.id === keyId);
  if (!key) return;
  const limits = await showUsageLimitEditor({
    eyebrow: "My API Key limit",
    title: "设置我的 Key 用量限制",
    description: "这里仅保存你为此 Key 设置的用量上限。",
    current: key.usageLimit?.userLimits,
    note: "设置后只会收紧此 Key 的可用额度；服务仍会遵守系统与账户级保护。"
  });
  if (!limits) return;
  try {
    await api(`/api/api-keys/${encodeURIComponent(keyId)}/usage-limits`, {
      method: "PATCH",
      body: JSON.stringify(limits)
    });
    toast("Key 用量限制已保存；实际仍受账户总限制约束。", "success");
    await loadUserApiKeys();
    renderAppKeys();
  } catch (error) { toast((error as Error).message, "error"); }
}

/** 用户自己的点数余额与钱包履历 */
function buildAppWalletQuery(limit = state.appWalletPagination.pageSize, includePage = true): string {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (includePage) params.set("page", String(state.appWalletPagination.page));
  if (state.appWalletFilters.from) params.set("from", state.appWalletFilters.from);
  if (state.appWalletFilters.to) params.set("to", state.appWalletFilters.to);
  if (state.appWalletFilters.type) params.set("type", state.appWalletFilters.type);
  return params.toString();
}

function triggerDownload(path: string): void {
  const link = document.createElement("a");
  link.href = path;
  link.download = "";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

async function loadAppWalletData(): Promise<void> {
  const walletData = await api<{
    wallet: TokenWallet;
    transactions: WalletTransaction[];
    pagination: PaginationMetadata;
  }>(`/api/wallet?${buildAppWalletQuery()}`);
  state.appWallet = walletData.wallet;
  state.appWalletTransactions = walletData.transactions || [];
  state.appWalletPagination = walletData.pagination || {
    page: 1,
    pageSize: state.appWalletPagination.pageSize,
    total: state.appWalletTransactions.length,
    totalPages: 1
  };
}

async function loadAppOrdersData(): Promise<void> {
  const ordersData = await api<{ orders: Order[] }>("/api/orders?limit=20");
  state.appOrders = ordersData.orders || [];
}

async function loadAppEntitlementsData(): Promise<void> {
  const [entitlements, funding] = await Promise.all([
    api<MemberEntitlementsSummary>("/api/entitlements?type=monthly_pass"),
    api<BillingFundingSummary>("/api/billing/funding-summary")
  ]);
  state.appEntitlements = entitlements;
  state.appFundingSummary = funding;
}

function entitlementTitle(entitlement: MemberEntitlement): string {
  const name = entitlement.entitlementSnapshot?.name;
  return typeof name === "string" && name.trim() ? name : "月卡";
}

function entitlementStatusText(status: string): string {
  const labels: Record<string, string> = {
    purchased: "待启用",
    active: "使用中",
    exhausted: "已耗尽",
    expired: "已过期",
    revoked: "已撤销"
  };
  return labels[status] || status;
}

function renderEntitlementCard(entitlement: MemberEntitlement, { canActivate = false } = {}): string {
  const used = Number(entitlement.consumedPoints || 0) + Number(entitlement.reservedPoints || 0);
  const percent = entitlement.totalPoints > 0 ? Math.min(100, Math.round(used * 100 / entitlement.totalPoints)) : 0;
  return `
    <article class="overview-card entitlement-card">
      <p class="eyebrow">${escapeHtml(entitlementStatusText(entitlement.status))}</p>
      <h3>${escapeHtml(entitlementTitle(entitlement))}</h3>
      <p class="overview-value">${formatWalletCredits(entitlement.availablePoints)}</p>
      <p class="muted">总额 ${formatWalletCredits(entitlement.totalPoints)} · 已用 ${formatWalletCredits(entitlement.consumedPoints)} · 预占 ${formatWalletCredits(entitlement.reservedPoints)}</p>
      <div class="usage-bar"><span style="width:${percent}%"></span></div>
      <p class="muted">购买 ${escapeHtml(formatTime(entitlement.purchasedAt))}</p>
      ${entitlement.activatedAt ? `<p class="muted">启用 ${escapeHtml(formatTime(entitlement.activatedAt))}</p>` : ""}
      ${entitlement.expiresAt ? `<p class="muted">到期 ${escapeHtml(formatTime(entitlement.expiresAt))}</p>` : ""}
      ${entitlement.activateBefore ? `<p class="muted">最晚启用 ${escapeHtml(formatTime(entitlement.activateBefore))}</p>` : ""}
      ${canActivate ? `<button class="primary-button" data-action="activate-entitlement" data-id="${escapeHtml(entitlement.id)}">启用月卡</button>` : ""}
    </article>
  `;
}

function renderAppEntitlementsContent(): void {
  const content = document.querySelector<HTMLElement>("#appEntitlementsContent");
  if (!content) return;
  const summary = state.appEntitlements || { activeEntitlement: null, pendingEntitlements: [], history: [] };
  const hasActive = Boolean(summary.activeEntitlement);
  content.innerHTML = `
    <section class="panel panel-fill">
      <div class="panel-heading">
        <div><p class="eyebrow">Monthly Pass</p><h2>月卡</h2></div>
        <button class="secondary-button" data-action="refresh-entitlements">刷新</button>
      </div>
      <section class="overview-grid wallet-overview">
        ${summary.activeEntitlement ? renderEntitlementCard(summary.activeEntitlement) : `
          <article class="overview-card"><p class="eyebrow">当前月卡</p><p class="overview-value">未启用</p><p class="muted">购买后需要手动启用才会开始计时。</p></article>
        `}
        <article class="overview-card">
          <p class="eyebrow">总可用 Credits</p>
          <p class="overview-value">${formatWalletCredits(state.appFundingSummary?.totalAvailablePoints || 0)}</p>
          <p class="muted">消费顺序：月卡 → 钱包</p>
        </article>
      </section>
    </section>
    <section class="panel panel-fill">
      <div class="panel-heading"><div><p class="eyebrow">Pending</p><h2>待启用月卡</h2></div></div>
      ${summary.pendingEntitlements.length
        ? `<section class="overview-grid wallet-overview">${summary.pendingEntitlements.map((item) =>
            renderEntitlementCard(item, { canActivate: !hasActive })
          ).join("")}</section>${hasActive ? `<p class="muted pad">当前已有活动月卡，到期或额度用完后可启用新的月卡。</p>` : ""}`
        : `<p class="muted pad">暂无待启用月卡。</p>`}
    </section>
    <section class="panel panel-fill">
      <div class="panel-heading"><div><p class="eyebrow">History</p><h2>历史月卡</h2></div></div>
      ${summary.history.length
        ? `<section class="overview-grid wallet-overview">${summary.history.map((item) => renderEntitlementCard(item)).join("")}</section>`
        : `<p class="muted pad">暂无历史月卡。</p>`}
    </section>
  `;
}

export async function renderAppEntitlements(): Promise<void> {
  if (!els.appContent) return;
  els.appContent.innerHTML = `<div id="appEntitlementsContent" class="list"><p class="muted pad">加载中...</p></div>`;
  try {
    await loadAppEntitlementsData();
    renderAppEntitlementsContent();
  } catch (error) {
    const content = document.querySelector<HTMLElement>("#appEntitlementsContent");
    if (content) content.innerHTML = `<p class="form-error">${escapeHtml((error as Error).message)}</p>`;
  }
}

export async function refreshAppEntitlements(): Promise<void> {
  await loadAppEntitlementsData();
  if (state.appTab === "entitlements") renderAppEntitlementsContent();
}

export async function activateEntitlement(entitlementId: string): Promise<void> {
  if (!confirm("启用后立即开始计算一个月有效期，且不能暂停。确认启用？")) return;
  setBusy(true);
  try {
    await api(`/api/entitlements/${encodeURIComponent(entitlementId)}/activate`, { method: "POST" });
    toast("月卡已启用。", "success");
    await refreshAppEntitlements();
  } catch (error) {
    toast((error as Error).message, "error");
    await refreshAppEntitlements().catch(() => {});
  } finally {
    setBusy(false);
  }
}

function renderAppWalletContent(): void {
  const content = document.querySelector<HTMLElement>("#appWalletContent");
  if (!content) return;
  const filters = state.appWalletFilters;
  content.innerHTML = `
      <section class="overview-grid wallet-overview">
        <div class="overview-card">
          <p class="eyebrow">当前 Credits</p>
          <p class="overview-value">${escapeHtml(formatWalletCredits(state.appWallet?.availablePoints ?? state.appWallet?.balance ?? 0))}</p>
          <p class="muted">余额 ${escapeHtml(formatWalletCredits(state.appWallet?.balance || 0))} · 预占 ${escapeHtml(formatWalletCredits(state.appWallet?.reservedPoints || 0))}</p>
        </div>
        <div class="overview-card">
          <p class="eyebrow">累计到账 Credits</p>
          <p class="overview-value">${escapeHtml(formatWalletCredits(state.appWallet?.totalPurchased || 0))}</p>
          <p class="muted">${escapeHtml(`${fmtInt(state.appWallet?.totalPurchased || 0)} microcredits`)}</p>
        </div>
        <div class="overview-card">
          <p class="eyebrow">累计消耗 Credits</p>
          <p class="overview-value">${escapeHtml(formatWalletCredits(state.appWallet?.totalConsumed || 0))}</p>
          <p class="muted">${escapeHtml(`${fmtInt(state.appWallet?.totalConsumed || 0)} microcredits`)}</p>
        </div>
      </section>
      <section class="panel panel-fill">
        <div class="panel-heading">
          <div><p class="eyebrow">History</p><h2>钱包流水</h2></div>
          <div class="panel-actions">
            <button class="secondary-button" data-action="query-app-wallet">查询</button>
            <button class="secondary-button" data-action="export-app-wallet">导出</button>
          </div>
        </div>
        <p class="muted">钱包数值按 credits 展示，1 credit = 1,000,000 microcredits。</p>
        <section class="toolbar-grid wallet-filter-grid">
          <label>
            <span>开始日期</span>
            <input id="app-wallet-filter-from" type="date" value="${escapeHtml(filters.from)}">
          </label>
          <label>
            <span>结束日期</span>
            <input id="app-wallet-filter-to" type="date" value="${escapeHtml(filters.to)}">
          </label>
          <label>
            <span>类型</span>
            <select id="app-wallet-filter-type">
              <option value="" ${filters.type ? "" : "selected"}>全部类型</option>
              <option value="grant" ${filters.type === "grant" ? "selected" : ""}>充值</option>
              <option value="consume" ${filters.type === "consume" ? "selected" : ""}>消费</option>
              <option value="refund" ${filters.type === "refund" ? "selected" : ""}>退款</option>
            </select>
          </label>
        </section>
        ${renderWalletTransactionTable(state.appWalletTransactions, { emptyText: "暂无钱包履历。" })}
        ${renderAppWalletPagination()}
      </section>
    `;
}

function renderAppWalletPagination(): string {
  const { page, pageSize, total, totalPages } = state.appWalletPagination;
  const start = total ? (page - 1) * pageSize + 1 : 0;
  const end = Math.min(page * pageSize, total);
  return `
    <footer class="pagination app-wallet-pagination">
      <div class="pagination-summary">共 ${fmtInt(total)} 条，当前 ${fmtInt(start)}-${fmtInt(end)}</div>
      <div class="pagination-controls">
        <button class="pagination-button" data-action="change-app-wallet-page" data-page="${page - 1}" ${page <= 1 ? "disabled" : ""}>上一页</button>
        <span class="pagination-page-status">第 ${fmtInt(page)} / ${fmtInt(totalPages)} 页</span>
        <button class="pagination-button" data-action="change-app-wallet-page" data-page="${page + 1}" ${page >= totalPages ? "disabled" : ""}>下一页</button>
      </div>
    </footer>
  `;
}

async function refreshAppWalletSection(): Promise<void> {
  await loadAppWalletData();
  if (state.appTab === "wallet") {
    renderAppWalletContent();
  }
}

export async function renderAppWallet(): Promise<void> {
  if (!els.appContent) return;
  els.appContent.innerHTML = `
    <section class="panel panel-fill">
      <div class="panel-heading">
        <div><p class="eyebrow">Wallet</p><h2>钱包履历</h2></div>
      </div>
      <div id="appWalletContent" class="list app-section-stack"><p class="muted pad">加载中...</p></div>
    </section>
  `;

  try {
    await loadAppWalletData();
    renderAppWalletContent();
  } catch (error) {
    const content = document.querySelector<HTMLElement>("#appWalletContent");
    if (content) content.innerHTML = `<p class="form-error">${escapeHtml((error as Error).message)}</p>`;
  }
}

function renderAppOrdersContent(): void {
  const content = document.querySelector<HTMLElement>("#appOrdersContent");
  if (!content) return;
  content.innerHTML = `
    <section class="panel panel-fill">
      <div class="panel-heading">
        <div><p class="eyebrow">Orders</p><h2>订单记录</h2></div>
      </div>
      ${renderAppOrderTable(state.appOrders)}
    </section>
  `;
}

export async function renderAppOrdersPage(): Promise<void> {
  if (!els.appContent) return;
  els.appContent.innerHTML = `<div id="appOrdersContent" class="list"><p class="muted pad">加载中...</p></div>`;
  try {
    await loadAppOrdersData();
    renderAppOrdersContent();
  } catch (error) {
    const content = document.querySelector<HTMLElement>("#appOrdersContent");
    if (content) content.innerHTML = `<p class="form-error">${escapeHtml((error as Error).message)}</p>`;
  }
}

async function refreshAppOrdersSection(): Promise<void> {
  await loadAppOrdersData();
  if (state.appTab === "orders") {
    renderAppOrdersContent();
  }
}

export async function queryAppWallet(): Promise<void> {
  const fromInput = document.querySelector<HTMLInputElement>("#app-wallet-filter-from");
  const toInput = document.querySelector<HTMLInputElement>("#app-wallet-filter-to");
  const typeInput = document.querySelector<HTMLSelectElement>("#app-wallet-filter-type");
  state.appWalletFilters = {
    memberId: "",
    from: fromInput?.value || "",
    to: toInput?.value || "",
    type: typeInput?.value || ""
  };
  state.appWalletPagination.page = 1;
  setBusy(true);
  try {
    await refreshAppWalletSection();
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export function exportAppWallet(): void {
  triggerDownload(`/api/wallet/export?${buildAppWalletQuery(10000, false)}`);
}

export async function changeAppWalletPage(page: string | number): Promise<void> {
  const nextPage = Math.max(1, Math.min(Number(page) || 1, state.appWalletPagination.totalPages));
  if (nextPage === state.appWalletPagination.page) return;
  state.appWalletPagination.page = nextPage;
  setBusy(true);
  try {
    await refreshAppWalletSection();
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

function renderAppOrderTable(orders: Order[]): string {
  if (!orders.length) {
    return `<p class="muted pad">暂无订单。</p>`;
  }
  return `
    <div class="table-wrap">
      <table class="sub-table">
        <thead>
          <tr>
            <th>订单 ID</th>
            <th>金额</th>
            <th>商品类型</th>
            <th>点数</th>
            <th>状态</th>
            <th>支付方式</th>
            <th>创建时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          ${orders.map((order) => `
            <tr>
              <td><code class="key-value">${escapeHtml(order.id)}</code></td>
              <td>¥${Number(order.amount).toFixed(2)}</td>
              <td>${escapeHtml(describeProductType(order.productType))}</td>
              <td>${fmtNum(order.tokenAmount)}</td>
              <td>${escapeHtml(describeOrderStatus(order.status))}</td>
              <td>${escapeHtml(order.paymentMethod || "-")}</td>
              <td class="table-muted">${escapeHtml(formatTime(order.createdAt))}</td>
              <td>
                ${["pending", "payment_pending", "payment_failed"].includes(order.status)
                  ? `<button class="primary-button" data-action="pay-order" data-id="${escapeHtml(order.id)}">${order.status === "payment_pending" ? "继续支付" : "去支付"}</button>`
                  : `<a class="ghost-button" href="/payments/alipay/return/?orderId=${encodeURIComponent(order.id)}">查看结果</a>`}
              </td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>
  `;
}

function describeProductType(type: string | null | undefined): string {
  return type === "monthly_pass" ? "月卡" : "钱包充值";
}

function describeOrderStatus(status: string): string {
  const labels: Record<string, string> = {
    pending: "待支付",
    payment_pending: "待付款确认",
    paid: "支付成功，到账中",
    fulfilled: "已到账",
    payment_failed: "支付失败",
    refund_pending: "退款中",
    refunded: "已退款",
    closed: "已关闭"
  };
  return labels[status] || status;
}

function formatTime(value: string | null | undefined): string {
  if (!value) return "-";
  return value.replace("T", " ").slice(0, 19);
}

function submitCheckoutForm(checkout: CheckoutPayload): void {
  if (checkout.mode !== "redirect_form" || !checkout.formAction) {
    throw new Error("当前页面仅支持网页支付跳转，请使用 App 客户端拉起支付。");
  }
  const form = document.createElement("form");
  form.method = checkout.formMethod || "POST";
  form.action = checkout.formAction;
  form.style.display = "none";
  Object.entries(checkout.fields || {}).forEach(([key, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

export async function handleBuyPricingPlan(planId: string): Promise<void> {
  setBusy(true);
  try {
    const created = await api<{ order: Order }>("/api/orders", {
      method: "POST",
      body: JSON.stringify({ planId })
    });
    if (created.order.status === "fulfilled" || Number(created.order.amount || 0) === 0) {
      toast(created.order.productType === "monthly_pass" ? "月卡已到账，请前往月卡页启用。" : "点数已到账钱包。", "success");
      await Promise.allSettled([loadAppWalletData(), refreshAppOrdersSection(), loadAppEntitlementsData()]);
      return;
    }
    await handlePayOrder(created.order.id);
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export async function handlePayOrder(orderId: string): Promise<void> {
  setBusy(true);
  try {
    const data = await api<{ order: Order; checkout: CheckoutPayload }>(`/api/orders/${encodeURIComponent(orderId)}/pay`, {
      method: "POST",
      body: JSON.stringify({ provider: "alipay" })
    });
    toast("正在跳转支付宝", "success");
    submitCheckoutForm(data.checkout);
  } catch (error) {
    toast((error as Error).message, "error");
    await refreshAppOrdersSection();
  } finally {
    setBusy(false);
  }
}

type PaymentResultTone = "success" | "pending" | "danger" | "neutral";

interface PaymentResultState {
  tone: PaymentResultTone;
  icon: string;
  eyebrow: string;
  title: string;
  description: string;
}

function paymentResultState(order: Order): PaymentResultState {
  if (order.status === "fulfilled") {
    return {
      tone: "success",
      icon: "✓",
      eyebrow: "Payment Complete",
      title: "支付成功",
      description: order.productType === "monthly_pass"
        ? "月卡已发放到您的账户，可前往控制台启用并查看额度。"
        : "充值点数已到账钱包，可立即开始使用。"
    };
  }
  if (order.status === "paid") {
    return {
      tone: "pending",
      icon: "↻",
      eyebrow: "Payment Confirmed",
      title: "支付已确认，正在到账",
      description: "平台已收到支付结果，正在为您发放商品，请稍候。"
    };
  }
  if (["pending", "payment_pending"].includes(order.status)) {
    return {
      tone: "pending",
      icon: "↻",
      eyebrow: "Payment Processing",
      title: "支付结果确认中",
      description: "正在向支付平台确认结果，请不要重复付款。"
    };
  }
  if (order.status === "payment_failed") {
    return {
      tone: "danger",
      icon: "!",
      eyebrow: "Payment Failed",
      title: "支付未完成",
      description: "本次支付没有成功，未产生扣款或到账记录。"
    };
  }
  if (order.status === "refund_pending") {
    return {
      tone: "pending",
      icon: "↻",
      eyebrow: "Refund Processing",
      title: "退款处理中",
      description: "退款申请已受理，到账时间以支付渠道处理结果为准。"
    };
  }
  if (order.status === "refunded") {
    return {
      tone: "neutral",
      icon: "i",
      eyebrow: "Refund Complete",
      title: "订单已退款",
      description: "退款已完成，请在原支付账户中核对资金变动。"
    };
  }
  if (order.status === "closed") {
    return {
      tone: "neutral",
      icon: "i",
      eyebrow: "Order Closed",
      title: "订单已关闭",
      description: "该订单已关闭，如仍需购买请返回产品页重新下单。"
    };
  }
  return {
    tone: "neutral",
    icon: "i",
    eyebrow: "Order Status",
    title: describeOrderStatus(order.status),
    description: "订单状态已更新，可前往订单记录查看完整信息。"
  };
}

function renderPaymentResultCard({
  tone,
  icon,
  eyebrow,
  title,
  description,
  content = "",
  loading = false
}: PaymentResultState & { content?: string; loading?: boolean }): string {
  const iconMarkup = loading
    ? `<span class="payment-result-spinner" aria-hidden="true"></span>`
    : escapeHtml(icon);
  return `
    <div class="payment-result-shell">
      <section class="payment-result-card payment-result-card-${tone}">
        <header class="payment-result-hero">
          <div class="payment-result-icon" aria-hidden="true">${iconMarkup}</div>
          <p class="payment-result-eyebrow">${escapeHtml(eyebrow)}</p>
          <h1>${escapeHtml(title)}</h1>
          <p class="payment-result-description">${escapeHtml(description)}</p>
        </header>
        ${content}
      </section>
      <p class="payment-result-support">如支付已完成但状态长时间未更新，请保留订单号并联系客服。</p>
    </div>
  `;
}

function renderPaymentResultOrder(order: Order): string {
  const result = paymentResultState(order);
  const productName = describeProductType(order.productType);
  const paymentMethod = order.paymentMethod === "alipay" ? "支付宝" : (order.paymentMethod || "支付宝");
  const completionTime = order.fulfilledAt || order.paidAt || order.refundedAt;
  const completionLabel = order.refundedAt ? "退款时间" : order.fulfilledAt ? "到账时间" : "支付时间";
  const content = `
    <div class="payment-result-details">
      <div class="payment-result-order-head">
        <div>
          <span class="payment-result-section-label">Order Summary</span>
          <strong>${escapeHtml(productName)}</strong>
          <small>${fmtNum(order.tokenAmount)} 点</small>
        </div>
        <div class="payment-result-amount">
          <span>订单金额</span>
          <strong>¥${Number(order.amount).toFixed(2)}</strong>
          <small>${escapeHtml(order.currency || "CNY")}</small>
        </div>
      </div>
      <dl class="payment-result-meta">
        <div><dt>订单编号</dt><dd><code>${escapeHtml(order.id)}</code></dd></div>
        <div><dt>商品类型</dt><dd>${escapeHtml(productName)}</dd></div>
        <div><dt>当前状态</dt><dd><span class="payment-result-status-badge">${escapeHtml(describeOrderStatus(order.status))}</span></dd></div>
        <div><dt>支付方式</dt><dd>${escapeHtml(paymentMethod)}</dd></div>
        <div><dt>创建时间</dt><dd>${escapeHtml(formatTime(order.createdAt))}</dd></div>
        ${completionTime ? `<div><dt>${completionLabel}</dt><dd>${escapeHtml(formatTime(completionTime))}</dd></div>` : ""}
      </dl>
      <div class="payment-result-notice">
        <span aria-hidden="true">i</span>
        <p>${result.tone === "success" ? "商品到账后即可在控制台查看；请勿将订单号或支付信息分享给他人。" : "订单状态可能因支付渠道同步产生短暂延迟，请不要重复付款。"}</p>
      </div>
      <div class="payment-result-actions">
        <a class="secondary-button large" href="/app/">进入控制台</a>
        <a class="primary-button large" href="/app/?tab=orders">查看订单记录</a>
      </div>
    </div>
  `;
  return renderPaymentResultCard({ ...result, content });
}

function renderPaymentResultMessage(options: PaymentResultState & { actionHref: string; actionLabel: string; orderId?: string }): string {
  const content = `
    <div class="payment-result-message">
      ${options.orderId ? `<p>订单编号 <code>${escapeHtml(options.orderId)}</code></p>` : ""}
      <a class="primary-button large" href="${escapeHtml(options.actionHref)}">${escapeHtml(options.actionLabel)}</a>
    </div>
  `;
  return renderPaymentResultCard({ ...options, content });
}

export async function initAlipayReturnPage(): Promise<void> {
  const container = document.querySelector<HTMLElement>("#alipayReturnContent");
  if (!container) return;
  const orderId = new URLSearchParams(window.location.search).get("orderId")?.trim() || "";
  if (!orderId) {
    document.title = "订单信息缺失 · SeekToken";
    container.innerHTML = renderPaymentResultMessage({
      tone: "danger",
      icon: "!",
      eyebrow: "Invalid Order",
      title: "无法查询支付结果",
      description: "当前链接缺少订单编号，请从订单记录重新进入。",
      actionHref: "/app/?tab=orders",
      actionLabel: "查看订单记录"
    });
    return;
  }

  try {
    const meData = await api<{ member: typeof state.user; csrfToken?: string }>("/api/auth/me");
    state.user = meData.member || null;
    state.csrfToken = meData.csrfToken || "";
  } catch {
    state.user = null;
    state.csrfToken = "";
  }

  document.title = "支付结果确认中 · SeekToken";
  container.innerHTML = renderPaymentResultCard({
    tone: "pending",
    icon: "↻",
    eyebrow: "Payment Verification",
    title: "正在确认支付结果",
    description: "正在与支付宝同步订单状态，请稍候。",
    loading: true,
    content: `<div class="payment-result-message"><p>订单编号 <code>${escapeHtml(orderId)}</code></p></div>`
  });

  const terminalStatuses = new Set(["fulfilled", "payment_failed", "closed", "refunded"]);
  for (let attempt = 0; attempt < 8; attempt += 1) {
    try {
      let data = await api<{ order: Order }>(`/api/orders/${encodeURIComponent(orderId)}`);
      if (["pending", "payment_pending", "paid"].includes(data.order.status)) {
        try {
          data = await api<{ order: Order }>(`/api/orders/${encodeURIComponent(orderId)}/payments/reconcile`, {
            method: "POST",
            body: JSON.stringify({})
          });
        } catch {
          // Keep showing the locally persisted order while the external channel is unavailable.
        }
      }
      const result = paymentResultState(data.order);
      document.title = `${result.title} · SeekToken`;
      container.innerHTML = renderPaymentResultOrder(data.order);
      if (terminalStatuses.has(data.order.status)) {
        return;
      }
    } catch (error) {
      const status = Number((error as { status?: number }).status || 0);
      if (status === 401) {
        document.title = "登录后查看支付结果 · SeekToken";
        container.innerHTML = renderPaymentResultMessage({
          tone: "neutral",
          icon: "i",
          eyebrow: "Sign In Required",
          title: "登录后查看支付结果",
          description: "为了保护订单信息，请先登录购买该商品的账户。",
          actionHref: "/login/",
          actionLabel: "去登录",
          orderId
        });
        return;
      }
      document.title = "支付结果查询失败 · SeekToken";
      container.innerHTML = renderPaymentResultMessage({
        tone: "danger",
        icon: "!",
        eyebrow: "Query Failed",
        title: "暂时无法查询支付结果",
        description: (error as Error).message,
        actionHref: "/app/?tab=orders",
        actionLabel: "查看订单记录",
        orderId
      });
      return;
    }

    await new Promise((resolve) => window.setTimeout(resolve, 2000));
  }

  document.title = "支付结果同步中 · SeekToken";
  container.innerHTML = renderPaymentResultMessage({
    tone: "pending",
    icon: "↻",
    eyebrow: "Still Processing",
    title: "支付结果仍在同步",
    description: "支付渠道暂未返回最终结果，请稍后到订单记录查看。",
    actionHref: "/app/?tab=orders",
    actionLabel: "查看订单记录",
    orderId
  });
}

function buildAppUsageQuery(): string {
  const params = new URLSearchParams();
  params.set("limit", String(state.appUsagePagination.pageSize));
  params.set("page", String(state.appUsagePagination.page));
  return params.toString();
}

async function loadAppUsageData(): Promise<void> {
  const data = await api<{ requestLogs: RequestLog[]; pagination: PaginationMetadata }>(
    `/api/usage?${buildAppUsageQuery()}`
  );
  state.appUsageRecords = data.requestLogs || [];
  state.appUsagePagination = data.pagination || {
    page: 1,
    pageSize: state.appUsagePagination.pageSize,
    total: state.appUsageRecords.length,
    totalPages: 1
  };
}

function renderAppUsageContent(): void {
  const list = document.querySelector<HTMLElement>("#appUsageList");
  if (!list) return;
  const logs = state.appUsageRecords;
  if (!logs.length) {
    list.innerHTML = `<p class="muted pad">暂无使用记录</p>`;
    return;
  }
  list.innerHTML = `${logs.map((log) => `
      <div class="list-item">
        <div>
          <strong>${escapeHtml(log.model || log.providerId || "?")}</strong>
          <span class="muted">${log.createdAt?.replace("T", " ").slice(0, 19) || ""}</span>
          ${log.error ? `<span class="pill pill-warning">错误</span>` : ""}
          ${log.usageEventStatus ? `<span class="pill">${escapeHtml(log.usageEventStatus)}</span>` : ""}
          ${log.apiKeyName ? `<span class="pill">${escapeHtml(log.apiKeyName)}</span>` : ""}
          <p class="muted">in ${fmtNum(log.inputTokens)} / cached ${fmtNum(log.rawCachedInputTokens || 0)} / out ${fmtNum(log.outputTokens)} / reasoning ${fmtNum(log.rawReasoningTokens || 0)}</p>
        </div>
        <div class="list-actions">
          <span class="muted">total ${fmtNum(log.totalTokens)}</span>
          <strong>${formatUsageCost(log)}</strong>
        </div>
      </div>
    `).join("")}${renderAppUsagePagination()}`;
}

function renderAppUsagePagination(): string {
  const { page, pageSize, total, totalPages } = state.appUsagePagination;
  const start = total ? (page - 1) * pageSize + 1 : 0;
  const end = Math.min(page * pageSize, total);
  return `
    <footer class="pagination app-usage-pagination">
      <div class="pagination-summary">共 ${fmtInt(total)} 条，当前 ${fmtInt(start)}-${fmtInt(end)}</div>
      <div class="pagination-controls">
        <button class="pagination-button" data-action="change-app-usage-page" data-page="${page - 1}" ${page <= 1 ? "disabled" : ""}>上一页</button>
        <span class="pagination-page-status">第 ${fmtInt(page)} / ${fmtInt(totalPages)} 页</span>
        <button class="pagination-button" data-action="change-app-usage-page" data-page="${page + 1}" ${page >= totalPages ? "disabled" : ""}>下一页</button>
      </div>
    </footer>
  `;
}

/** 使用记录(异步加载) */
export async function renderAppUsage(): Promise<void> {
  if (!els.appContent || !state.user) return;
  els.appContent.innerHTML = `<section class="panel panel-fill"><div class="panel-heading"><div><p class="eyebrow">Usage</p><h2>使用记录</h2></div></div><div id="appUsageList" class="list"><p class="muted pad">加载中...</p></div></section>`;
  try {
    await loadAppUsageData();
    renderAppUsageContent();
  } catch (error) {
    const list = document.querySelector<HTMLElement>("#appUsageList");
    if (list) list.innerHTML = `<p class="form-error">${escapeHtml((error as Error).message)}</p>`;
  }
}

export async function changeAppUsagePage(page: string | number): Promise<void> {
  const nextPage = Math.max(1, Math.min(Number(page) || 1, state.appUsagePagination.totalPages));
  if (nextPage === state.appUsagePagination.page) return;
  state.appUsagePagination.page = nextPage;
  setBusy(true);
  try {
    await loadAppUsageData();
    if (state.appTab === "usage") renderAppUsageContent();
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

const modelUsagePeriodLabels: Record<keyof ModelUsagePeriods, string> = {
  day: "当天",
  week: "本周",
  month: "本月",
  total: "历史"
};

async function loadAppModelUsage(): Promise<void> {
  state.appModelUsage = await api<AppModelUsage>("/api/app/model-usage");
}

function renderModelUsagePeriodButtons(action: string, selected: keyof ModelUsagePeriods): string {
  return `<div class="model-usage-periods">${(Object.keys(modelUsagePeriodLabels) as Array<keyof ModelUsagePeriods>).map((period) =>
    `<button class="ghost-button compact-button ${period === selected ? "active" : ""}" data-action="${action}" data-period="${period}">${modelUsagePeriodLabels[period]}</button>`
  ).join("")}</div>`;
}

function renderModelUsageKeySections(data: AppModelUsage, period: keyof ModelUsagePeriods): string {
  if (!data.apiKeys.length) return `<p class="muted pad">暂无 API Key。</p>`;
  return `<div class="model-usage-key-list">${data.apiKeys.map((key) => `
    <section class="model-usage-key-section">
      <header><div><h3>${escapeHtml(key.name)}</h3><p><code>${escapeHtml(key.keyPrefix)}</code></p></div><span>${fmtNum(key.periods[period].requests)} 次调用 · ${fmtNum(key.periods[period].totalTokens)} Token</span></header>
      ${renderModelUsageCharts(key.periods[period])}
    </section>
  `).join("")}</div>`;
}

function renderAppModelUsageContent(): void {
  const content = document.querySelector<HTMLElement>("#appModelUsageContent");
  const data = state.appModelUsage;
  if (!content || !data) return;
  const period = state.appModelUsagePeriod;
  const summary = data.periods[period];
  content.innerHTML = `
    <section class="panel panel-fill">
      <div class="panel-heading model-usage-heading">
        <div><p class="eyebrow">Model usage</p><h2>模型使用比例</h2><small>仅统计成功调用；分别按调用次数和 Token 总数计算占比。</small></div>
        ${renderModelUsagePeriodButtons("set-app-model-usage-period", period)}
      </div>
      <div class="model-usage-summary"><strong>${fmtNum(summary.requests)}</strong><span>成功调用</span><strong>${fmtNum(summary.totalTokens)}</strong><span>Token</span></div>
      ${renderModelUsageCharts(summary)}
    </section>
    <section class="panel panel-fill">
      <div class="panel-heading"><div><p class="eyebrow">API Keys</p><h2>各 Key 的模型使用比例</h2></div></div>
      ${renderModelUsageKeySections(data, period)}
    </section>
  `;
}

export async function renderAppModelUsage(): Promise<void> {
  if (!els.appContent) return;
  els.appContent.innerHTML = `<section class="panel panel-fill"><div id="appModelUsageContent" class="list"><p class="muted pad">加载中...</p></div></section>`;
  try {
    await loadAppModelUsage();
    renderAppModelUsageContent();
  } catch (error) {
    const content = document.querySelector<HTMLElement>("#appModelUsageContent");
    if (content) content.innerHTML = `<p class="form-error">${escapeHtml((error as Error).message)}</p>`;
  }
}

export function setAppModelUsagePeriod(period: string): void {
  if (!Object.hasOwn(modelUsagePeriodLabels, period)) return;
  state.appModelUsagePeriod = period as keyof ModelUsagePeriods;
  if (state.appTab === "modelUsage") renderAppModelUsageContent();
}

function formatUsageCost(log: {
  billedCost?: number | null;
  billedTokens?: number | null;
  billingCurrency?: string | null;
  pricingUnit?: string | null;
}): string {
  if (log.billedCost !== null && log.billedCost !== undefined) {
    const value = Number(log.billedCost);
    const display = value >= 1 ? value.toFixed(3) : value.toFixed(6);
    return `${display} ${escapeHtml(log.billingCurrency || "credits")}`;
  }
  if (log.billedTokens !== null && log.billedTokens !== undefined) {
    return `${fmtNum(log.billedTokens)} billed`;
  }
  return "未计费";
}

/** 账户设置 + 修改密码 */
export function renderAppSettings(): void {
  if (!els.appContent) return;
  els.appContent.innerHTML = `
    <section class="panel panel-fill">
      <div class="panel-heading">
        <div><p class="eyebrow">Account</p><h2>账户设置</h2></div>
      </div>
      <div class="user-info">
        <p><strong>用户名</strong> ${escapeHtml(state.user?.name || "")}</p>
        <p><strong>邮箱</strong> ${escapeHtml(state.user?.email || "")}</p>
        <p><strong>计划</strong> ${escapeHtml(state.user?.plan || "trial")}</p>
        <p><strong>角色</strong> ${parseUserRoles(state.user).join(", ")}</p>
      </div>
    </section>
    <section class="panel panel-fill">
      <div class="panel-heading">
        <div><p class="eyebrow">Security</p><h2>修改密码</h2></div>
      </div>
      <form id="changePasswordForm" class="stack" style="max-width:480px; padding: 14px 16px;">
        <label class="auth-field">
          <span>当前密码</span>
          <input id="currentPassword" type="password" required autocomplete="current-password">
        </label>
        <label class="auth-field">
          <span>新密码</span>
          <input id="newPassword" type="password" placeholder="至少 8 位" required autocomplete="new-password">
        </label>
        <label class="auth-field">
          <span>确认新密码</span>
          <input id="confirmPassword" type="password" required autocomplete="new-password">
        </label>
        <footer class="modal-actions">
          <button type="submit" class="primary-button">修改密码</button>
        </footer>
      </form>
    </section>
  `;
  const form = document.querySelector<HTMLFormElement>("#changePasswordForm");
  if (form) {
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const currentPassword = document.querySelector<HTMLInputElement>("#currentPassword")?.value ?? "";
      const newPassword = document.querySelector<HTMLInputElement>("#newPassword")?.value ?? "";
      const confirmPassword = document.querySelector<HTMLInputElement>("#confirmPassword")?.value ?? "";
      if (newPassword !== confirmPassword) {
        toast("两次新密码不一致", "error");
        return;
      }
      try {
        await api("/api/auth/change-password", {
          method: "POST",
          body: JSON.stringify({ currentPassword, newPassword })
        });
        toast("密码已修改,其他设备已登出", "success");
        form.reset();
      } catch (error) {
        toast((error as Error).message, "error");
      }
    });
  }
}

/** 打开用户自助创建 Key 的弹窗(复用 #apiKeyModal) */
export function openUserApiKeyModal(): void {
  state.pendingApiKeyMemberId = "";
  state.pendingApiKeyIsUser = true;
  if (els.apiKeyName) els.apiKeyName.value = "";
  openModal("apiKey");
}

/** 用户自助创建 API Key */
export async function handleCreateUserKey(): Promise<void> {
  const name = els.apiKeyName?.value.trim() ?? "";
  if (!name) {
    toast("请输入 Key 名称。");
    return;
  }
  setBusy(true);
  try {
    const data = await api<{ secret: string; apiKey: { id: string } }>("/api/api-keys", {
      method: "POST",
      body: JSON.stringify({ name })
    });
    if (els.apiKeyName) els.apiKeyName.value = "";
    state.pendingApiKeyIsUser = false;
    closeModal();
    // 保存明文到本地,便于后续复制(仅本机 localStorage,不上传服务器)
    state.plainApiKeys[data.apiKey.id] = data.secret;
    saveStoredPlainApiKeys(state.plainApiKeys);
    if (els.plainKey) els.plainKey.textContent = data.secret;
    openModal("key");
    toast("API Key 已生成,只显示这一次。后续可在列表中复制。", "success");
    await loadUserApiKeys();
    if (state.appTab === "keys") renderAppKeys();
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

/** 用户删除自己的 API Key */
export async function handleDeleteUserKey(keyId: string): Promise<void> {
  if (!confirm("确认删除此 API Key?删除后无法恢复。")) return;
  try {
    await api(`/api/api-keys/${encodeURIComponent(keyId)}`, { method: "DELETE" });
    // 清理本地存储的明文
    delete state.plainApiKeys[keyId];
    saveStoredPlainApiKeys(state.plainApiKeys);
    toast("已删除", "success");
    await loadUserApiKeys();
    if (state.appTab === "keys") renderAppKeys();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

/** 用户停用/启用自己的 API Key */
export async function handleToggleUserKey(keyId: string, currentStatus: string): Promise<void> {
  const nextStatus = currentStatus === "active" ? "disabled" : "active";
  const label = nextStatus === "active" ? "启用" : "停用";
  if (!confirm(`确认${label}此 API Key?`)) return;
  try {
    await api(`/api/api-keys/${encodeURIComponent(keyId)}`, {
      method: "PATCH",
      body: JSON.stringify({ status: nextStatus })
    });
    toast(`已${label}`, "success");
    await loadUserApiKeys();
    if (state.appTab === "keys") renderAppKeys();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

/** 用户获取自己 API Key 的明文 */
export async function fetchUserApiKey(keyId: string): Promise<void> {
  setBusy(true);
  try {
    const data = await api<{ plainKey: string }>(`/api/api-keys/${encodeURIComponent(keyId)}/plain`);
    state.plainApiKeys[keyId] = data.plainKey;
    saveStoredPlainApiKeys(state.plainApiKeys);
    toast("API Key 已获取。", "success");
    if (state.appTab === "keys") renderAppKeys();
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

/** 切换显示/隐藏完整 API Key */
export function toggleShowUserKey(keyId: string): void {
  const el = document.querySelector<HTMLElement>(`#full-key-${CSS.escape(keyId)}`);
  if (el) {
    el.classList.toggle("hidden");
  }
}

/** 用户复制自己的 API Key(有明文复制明文,否则复制前缀) */
export async function handleCopyUserKey(keyId: string): Promise<void> {
  const plainKey = state.plainApiKeys[keyId];
  if (plainKey) {
    return copyText(plainKey, "完整 API Key 已复制。");
  }
  const apiKey = (state.userApiKeys || []).find((k) => k.id === keyId);
  return copyText(
    apiKey?.keyPrefix || "",
    "只复制了 Key 前缀；请先点击「获取完整 Key」。"
  );
}

/** 加载当前用户的 API Key 列表 */
export async function loadUserApiKeys(): Promise<void> {
  if (!state.user) return;
  try {
    const data = await api<{ apiKeys: ApiKey[] }>("/api/api-keys");
    state.userApiKeys = data.apiKeys || [];
  } catch {
    state.userApiKeys = [];
  }
}

/** 用户登出 */
export async function handleAppLogout(): Promise<void> {
  try {
    await api("/api/auth/logout", { method: "POST" });
  } catch {
    // 忽略登出请求错误
  }
  state.user = null;
  state.csrfToken = "";
  state.userApiKeys = [];
  state.appWallet = null;
  state.appWalletTransactions = [];
  state.appOrders = [];
  toast("已登出");
  window.location.href = "/";
}
